import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { glyphFor } from './DirectionsList';
import {
  buildRouteIndex,
  computeNavState,
  dynamicZoom,
} from '../lib/navEngine';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

/**
 * NavigationView — map-first live navigation.
 *
 * The map fills the screen and the car sits in the lower third so there's
 * road visible ahead. Camera is heading-up by default (rotates to direction
 * of travel); a compass button toggles north-up. Only the MAP rotates — the
 * maneuver banner and overlays stay upright.
 *
 * Position comes from one of two sources:
 *   - real GPS via navigator.geolocation.watchPosition (when driving)
 *   - simulation mode, which "drives" the route polyline so the whole thing
 *     can be tested from a desk
 *
 * Props:
 *   route    — processValhallaResponse output (needs geometry_full, maneuvers)
 *   stops    — charging stops array (may be empty until station DB populated)
 *   socTrace — SoC curve, for the live battery overlay
 *   vehicle  — for display
 *   locale   — distance/unit formatting
 */
export default function NavigationView({ route, stops = [], socTrace, vehicle, locale }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const carMarkerRef = useRef(null);
  const routeIndexRef = useRef(null);
  const hintIndexRef = useRef(0);
  const prevPosRef = useRef(null);
  const watchIdRef = useRef(null);
  const simTimerRef = useRef(null);

  const [navState, setNavState] = useState(null);
  const [headingUp, setHeadingUp] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [gpsError, setGpsError] = useState('');
  const [mapReady, setMapReady] = useState(false);

  const geometry = route?.geometry_full || route?.geometry_simplified || [];
  const maneuvers = route?.maneuvers || [];

  // Build the cumulative-distance index once for this route.
  useEffect(() => {
    if (geometry.length > 1) {
      routeIndexRef.current = buildRouteIndex(geometry);
    }
  }, [route]);

  // ------------------------------------------------------------------
  // MAP SETUP
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (geometry.length < 2) return;
    if (!import.meta.env.VITE_MAPBOX_TOKEN) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: geometry[0],
      zoom: 15,
      pitch: 55,                 // tilted view — more "driving" feel
      attributionControl: false,
    });
    mapRef.current = map;

    map.on('load', () => {
      // Route line
      map.addSource('nav-route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: geometry },
        },
      });
      map.addLayer({
        id: 'nav-route-line',
        type: 'line',
        source: 'nav-route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#20d9a6', 'line-width': 6, 'line-opacity': 0.9 },
      });

      // Charging stop markers
      stops.forEach((s) => {
        if (!s.latitude || !s.longitude) return;
        new mapboxgl.Marker({ color: '#ffb84d' })
          .setLngLat([s.longitude, s.latitude])
          .addTo(map);
      });

      // Car marker — a simple triangle that points in the heading direction.
      const el = document.createElement('div');
      el.style.cssText =
        'width:0;height:0;border-left:11px solid transparent;' +
        'border-right:11px solid transparent;border-bottom:26px solid #20d9a6;' +
        'filter:drop-shadow(0 2px 4px rgba(0,0,0,0.6));';
      carMarkerRef.current = new mapboxgl.Marker({ element: el, rotationAlignment: 'map' })
        .setLngLat(geometry[0])
        .addTo(map);

      setMapReady(true);
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [route]);

  // ------------------------------------------------------------------
  // CAMERA — re-centre and rotate the map on each position update.
  // The car is anchored in the lower third (offset), not the centre,
  // so the driver sees road ahead.
  // ------------------------------------------------------------------
  const updateCamera = useCallback((position, heading, distToNextKm, speedKmh) => {
    const map = mapRef.current;
    if (!map) return;
    const zoom = dynamicZoom(distToNextKm, speedKmh);
    map.easeTo({
      center: position,
      zoom,
      bearing: headingUp ? heading : 0,
      pitch: 55,
      // Negative y offset pushes the centre point UP, so the car marker
      // (placed at `center`) sits in the lower third of the viewport.
      offset: [0, map.getContainer().clientHeight * 0.22],
      duration: 900,
      easing: (t) => t,
    });
  }, [headingUp]);

  // ------------------------------------------------------------------
  // POSITION HANDLER — shared by real GPS and simulation.
  // ------------------------------------------------------------------
  const onPosition = useCallback((position, speedKmh = 0) => {
    if (!routeIndexRef.current) return;
    const state = computeNavState(
      route,
      routeIndexRef.current,
      position,
      prevPosRef.current,
      stops,
      hintIndexRef.current
    );
    hintIndexRef.current = state.snapped_index;
    prevPosRef.current = position;
    setNavState({ ...state, speed_kmh: speedKmh });

    if (carMarkerRef.current) {
      carMarkerRef.current.setLngLat(position);
      // Rotate the car glyph. In heading-up mode the map itself rotates, so
      // the car points "up" (0). In north-up mode the car must show heading.
      const el = carMarkerRef.current.getElement();
      el.style.transform = el.style.transform.replace(/rotate\([^)]*\)/, '') +
        ` rotate(${headingUp ? 0 : state.heading_deg}deg)`;
    }
    updateCamera(position, state.heading_deg, state.dist_to_next_maneuver_km, speedKmh);
  }, [route, stops, headingUp, updateCamera]);

  // ------------------------------------------------------------------
  // REAL GPS
  // ------------------------------------------------------------------
  const startRealGps = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setGpsError('Location not supported on this device');
      return;
    }
    setGpsError('');
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const speedKmh = pos.coords.speed != null && pos.coords.speed >= 0
          ? pos.coords.speed * 3.6
          : 0;
        onPosition([pos.coords.longitude, pos.coords.latitude], speedKmh);
      },
      (err) => {
        setGpsError(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission denied — enable it to navigate'
            : 'Waiting for GPS signal…'
        );
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 }
    );
  }, [onPosition]);

  // ------------------------------------------------------------------
  // SIMULATION — "drives" the route polyline so navigation can be tested
  // without being in a moving vehicle. Steps along geometry_full at a
  // steady pace and feeds each point through the same onPosition handler.
  // ------------------------------------------------------------------
  const startSimulation = useCallback(() => {
    if (geometry.length < 2) return;
    setSimulating(true);
    setGpsError('');
    let i = 0;
    // Advance a few vertices per tick so a long route doesn't take forever.
    const stride = Math.max(1, Math.floor(geometry.length / 600));
    simTimerRef.current = setInterval(() => {
      if (i >= geometry.length) {
        clearInterval(simTimerRef.current);
        setSimulating(false);
        return;
      }
      // A plausible simulated speed so dynamic zoom behaves realistically.
      onPosition(geometry[i], 60);
      i += stride;
    }, 700);
  }, [geometry, onPosition]);

  const stopSimulation = useCallback(() => {
    if (simTimerRef.current) clearInterval(simTimerRef.current);
    setSimulating(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      if (simTimerRef.current) clearInterval(simTimerRef.current);
    };
  }, []);

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------
  if (!import.meta.env.VITE_MAPBOX_TOKEN) {
    return (
      <div style={fallbackStyle}>
        Navigation needs a map — VITE_MAPBOX_TOKEN is not set.
      </div>
    );
  }

  const activeMan = maneuvers[navState?.active_maneuver_index ?? 0];
  const nextMan = maneuvers[navState?.next_maneuver_index ?? 0];
  const distToNext = navState?.dist_to_next_maneuver_km ?? 0;
  const charger = navState?.next_charger;
  const currentSoc = estimateLiveSoc(socTrace, navState?.distance_along_km);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* The map fills everything. Only this layer rotates. */}
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

      {/* ---- TOP: next-maneuver banner (stays upright) ---- */}
      {nextMan && (
        <div style={bannerStyle}>
          <div style={{ fontSize: 30, color: '#20d9a6', lineHeight: 1, flexShrink: 0 }}>
            {glyphFor(nextMan.type)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
              {distToNext < 1
                ? `${Math.round(distToNext * 1000)} m`
                : `${distToNext.toFixed(1)} km`}
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', lineHeight: 1.3 }}>
              {nextMan.instruction}
            </div>
          </div>
        </div>
      )}

      {/* ---- COMPASS toggle (heading-up / north-up) ---- */}
      <button
        onClick={() => setHeadingUp((v) => !v)}
        title={headingUp ? 'Heading-up (tap for North-up)' : 'North-up (tap for Heading-up)'}
        style={compassStyle}
      >
        {headingUp ? '🧭' : 'N'}
      </button>

      {/* ---- BOTTOM-LEFT: live SoC + remaining distance ---- */}
      <div style={socOverlayStyle}>
        <div style={{ fontSize: 22, fontWeight: 600, color: '#20d9a6', lineHeight: 1 }}>
          {currentSoc != null ? `${Math.round(currentSoc)}%` : '—'}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>
          {navState
            ? `${formatKm(navState.distance_remaining_km, locale)} left`
            : 'battery'}
        </div>
      </div>

      {/* ---- BOTTOM-RIGHT: next charger countdown ---- */}
      <div style={chargerOverlayStyle}>
        {charger ? (
          <>
            <div style={{ fontSize: 10, color: 'var(--accent-warning)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Next charger
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#fff', marginTop: 2 }}>
              {formatKm(charger.distance_km, locale)}
            </div>
            {charger.stop?.name && (
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 1, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {charger.stop.name}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            No charging stops on this route
          </div>
        )}
      </div>

      {/* ---- START / SIMULATE controls ---- */}
      {!navState && mapReady && (
        <div style={startOverlayStyle}>
          <button onClick={startRealGps} style={primaryBtnStyle}>
            Start navigation
          </button>
          <button onClick={startSimulation} style={secondaryBtnStyle}>
            ▶ Simulate drive
          </button>
          {gpsError && (
            <div style={{ fontSize: 11, color: 'var(--accent-danger)', marginTop: 8, textAlign: 'center' }}>
              {gpsError}
            </div>
          )}
        </div>
      )}

      {/* ---- Simulation running indicator ---- */}
      {simulating && (
        <button onClick={stopSimulation} style={simBadgeStyle}>
          ● SIMULATING — tap to stop
        </button>
      )}

      {/* ---- GPS error while navigating ---- */}
      {navState && gpsError && (
        <div style={gpsErrorStyle}>{gpsError}</div>
      )}
    </div>
  );
}

// ===========================================================================
// HELPERS
// ===========================================================================

/**
 * Estimate the live SoC at the current distance along the route by
 * interpolating the SoC trace. The trace has points keyed by km travelled.
 */
function estimateLiveSoc(socTrace, distanceAlongKm) {
  const pts = socTrace?.points;
  if (!Array.isArray(pts) || pts.length === 0) return null;
  if (distanceAlongKm == null) return pts[0]?.soc_pct ?? null;
  // Find the bracketing points and interpolate
  let prev = pts[0];
  for (const p of pts) {
    const km = p.km_in ?? p.distance_km ?? 0;
    if (km >= distanceAlongKm) {
      const pk = prev.km_in ?? prev.distance_km ?? 0;
      const span = km - pk;
      if (span <= 0) return p.soc_pct;
      const f = (distanceAlongKm - pk) / span;
      return (prev.soc_pct ?? 0) + f * ((p.soc_pct ?? 0) - (prev.soc_pct ?? 0));
    }
    prev = p;
  }
  return pts[pts.length - 1]?.soc_pct ?? null;
}

function formatKm(km, locale) {
  if (km == null) return '—';
  const useMiles = locale?.distance_unit === 'mi';
  if (useMiles) {
    const mi = km * 0.621371;
    return mi < 1 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(mi < 10 ? 1 : 0)} mi`;
  }
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(km < 10 ? 1 : 0)} km`;
}

// ===========================================================================
// STYLES — overlays are all position:absolute over the map, and upright.
// ===========================================================================

const fallbackStyle = {
  height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--bg-card)', borderRadius: 12, color: 'var(--text-tertiary)',
  fontSize: 12, padding: 16, textAlign: 'center',
};

const bannerStyle = {
  position: 'absolute', top: 12, left: 12, right: 12,
  background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(10px)',
  border: '0.5px solid var(--border-default)', borderRadius: 14,
  padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 14,
  zIndex: 10,
};

const compassStyle = {
  position: 'absolute', top: 92, right: 12,
  width: 40, height: 40, borderRadius: 20,
  background: 'rgba(10,10,10,0.92)', border: '0.5px solid var(--border-default)',
  color: '#fff', fontSize: 16, cursor: 'pointer', zIndex: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const socOverlayStyle = {
  position: 'absolute', bottom: 14, left: 12,
  background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(10px)',
  border: '0.5px solid var(--border-default)', borderRadius: 12,
  padding: '10px 14px', zIndex: 10, minWidth: 76,
};

const chargerOverlayStyle = {
  position: 'absolute', bottom: 14, right: 12,
  background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(10px)',
  border: '0.5px solid var(--border-default)', borderRadius: 12,
  padding: '10px 14px', zIndex: 10, textAlign: 'right',
};

const startOverlayStyle = {
  position: 'absolute', left: '50%', bottom: 90, transform: 'translateX(-50%)',
  display: 'flex', flexDirection: 'column', gap: 8, zIndex: 20,
};

const primaryBtnStyle = {
  background: '#20d9a6', color: '#000', border: 'none', borderRadius: 24,
  padding: '12px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const secondaryBtnStyle = {
  background: 'rgba(10,10,10,0.92)', color: '#fff',
  border: '0.5px solid var(--border-default)', borderRadius: 24,
  padding: '10px 24px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
};

const simBadgeStyle = {
  position: 'absolute', top: 92, left: 12,
  background: 'rgba(255,184,77,0.15)', color: 'var(--accent-warning)',
  border: '0.5px solid var(--accent-warning)', borderRadius: 20,
  padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', zIndex: 10,
};

const gpsErrorStyle = {
  position: 'absolute', top: 92, left: '50%', transform: 'translateX(-50%)',
  background: 'rgba(255,87,87,0.15)', color: 'var(--accent-danger)',
  border: '0.5px solid var(--accent-danger)', borderRadius: 8,
  padding: '6px 12px', fontSize: 11, zIndex: 10, whiteSpace: 'nowrap',
};
